import { Epostmal, Ordrebekreftelse, ProduktSpesifikasjon, Vedlegg } from "codegen";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";

import { Direction, Flex, Justify } from "@ntemarked/mittne-components/dist/Flex/Flex";

import LoadingAnimation from "components/organisms/LoadingAnimation";
import { MainLayoutPageHeader } from "components/organisms/MainLayout";
import { Table } from "components/organisms/Table";
import { useStrombestillingApiQuery } from "hooks/useStrombestillingApi";

export default function EditOrderConfirmationPage({
	products,
	orderConfirmations,
}: {
	readonly products: ProduktSpesifikasjon[];
	readonly orderConfirmations: Ordrebekreftelse[];
}) {
	const { orderConfirmationId } = useParams<{ orderConfirmationId: string }>();
	const { tariffId } = Object.fromEntries(new URLSearchParams(window?.location?.search));

	const [selectedTariffId, setSelectedTariffId] = useState<string | null>(tariffId || null);

	const orderConfirmation = useMemo(() => {
		return orderConfirmations.find((c) => c.id === orderConfirmationId);
	}, [orderConfirmations, orderConfirmationId]);

	const filteredProducts = useMemo(() => {
		return products.filter((p) => p?.ordrebekreftelse === orderConfirmationId);
	}, [products, orderConfirmationId]);

	const endpoint = orderConfirmation?.epostUrl?.split(import.meta.env.VITE_STROEMBESTILLING_API_URL as string)[1] ?? "";
	const { data: email, isLoading: emailIsLoading } = useStrombestillingApiQuery<Epostmal>(endpoint, [endpoint]);

	return (
		<Flex justifyContent={Justify.spaceBetween} direction={Direction.column} gap="2rem">
			<TitleLabel>Ordrebekreftelse for gruppe:</TitleLabel>
			<MainLayoutPageHeader title={orderConfirmation?.navn} />
			{filteredProducts?.length > 0 && (
				<label>
					Vis med produkt
					<br />
					<StyledSelect
						aria-label="produkt"
						title="Produkt"
						name="product"
						defaultValue={filteredProducts.find((p) => p.tariffId === selectedTariffId)?.ucProductId ?? ""}
						onChange={(e) => {
							const selectedOption = Array.from(e.target.options).find((option) => option.selected);
							setSelectedTariffId(products.find((p) => p.ucProductId === selectedOption?.value)?.tariffId ?? null);
						}}
					>
						{filteredProducts?.map((p) => (
							<option key={p.ucProductId} value={p.ucProductId}>
								{p.navn ?? "Ukjent produkt"}
							</option>
						))}
						<option value="">Eksempelprodukt</option>
					</StyledSelect>
				</label>
			)}
			<AttachementsContainer>
				<h2>Vedlegg</h2>
				{(orderConfirmation?.vedlegg?.length && (
					<Table
						entries={orderConfirmation.vedlegg}
						labels={{ filnavn: "Filnavn", tariffId: "Produkt" }}
						generators={{
							filnavn: (vedlegg: Vedlegg) => (
								<>
									{vedlegg && (
										<a
											target="_blank"
											rel="noreferrer"
											href={`${import.meta.env.VITE_STROEMBESTILLING_API_URL}/templates?document=${orderConfirmation.id}&attachment=${
												vedlegg.id
											}${selectedTariffId ? "&tariffID=" + selectedTariffId : ""}`}
										>
											{vedlegg.filnavn}.pdf
										</a>
									)}
								</>
							),
							tariffId: () => <>{!selectedTariffId ? "Eksempelprodukt" : filteredProducts.find((p) => p?.tariffId === selectedTariffId)?.navn}</>,
						}}
					/>
				)) ?? <></>}
			</AttachementsContainer>
			<h2>Produkter</h2>
			<ProductsWrapper>
				{filteredProducts.map((product) => (
					<ProductLabel key={product.ucProductId}>{product.navn}</ProductLabel>
				))}
			</ProductsWrapper>
			{emailIsLoading && <LoadingAnimation />}
			{!emailIsLoading && email && (
				<EmailWrapper>
					<EmailTitle>
						{email.tittel}
						<img src="assets/icons/emailheader-title.svg" alt="Epost header avsender" />
					</EmailTitle>
					<EmailHeader>
						<img src="assets/icons/emailheader-sender.svg" alt="Epost avsender" />
						<img src="assets/icons/emailheader-buttons.svg" alt="Epost knapper" />
					</EmailHeader>
					<div dangerouslySetInnerHTML={{ __html: email.innhold! }} />
				</EmailWrapper>
			)}
		</Flex>
	);
}

const TitleLabel = styled.span`
	font-size: 20px;
	font-weight: 700;
	color: var(--nte-graa-mork);
`;
const StyledSelect = styled.select`
	width: max-content;
	margin: 0.5rem 0;
`;
const AttachementsContainer = styled.div`
	display: flex;
	flex-direction: column;
`;
const ProductsWrapper = styled.div`
	display: flexbox;
	gap: 1rem;
	margin-bottom: 2rem;
	flex-wrap: wrap;
`;
const ProductLabel = styled.label`
	padding: 1rem 1.5rem;
	border-radius: 9px;
	background: var(--nte-blaa-himmel);
	color: var(--nte-blaa-uu);
	font-weight: 700;
	display: flex;
	gap: 0.5rem;
`;
const EmailTitle = styled.h2`
	margin: 0;
	padding: 1rem;
	border: 1px solid #f0f0f0;
	font-family: Segoe UI;
	font-weight: 600;
	display: flex;
	gap: 1rem;
`;
const EmailWrapper = styled.div`
	border: 1px solid #f0f0f0;
`;
const EmailHeader = styled.div`
	padding: 1rem;
	display: flex;
	flex-direction: row;
	justify-content: space-between;
`;
